import React, { useState, useEffect, useMemo, useRef } from "react"
import * as DateFns from "date-fns"
import {
  Idea,
  IdeaFieldName,
  ideaIsEmpty,
} from "../../../../../model/types"
import * as Commands from "../../../../../model/commands"
import {
  EllipsisVerticalIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import { useNana } from "../../../../hooks/useNana"
import classNames from "classnames"
import { cellBorderColour } from "../../../../components/idea_box"
import { NavigationHistoryState } from "../../mosaic"
import useOutsideClick from "../../../../hooks/useOutsideClick"

type IdeaPaneFormData = {
  title?: string
  memo?: string
  colour?: string
}

const creationTimestamp = (idea: Idea): number => {
  const value =
    idea.createdAt instanceof Date
      ? idea.createdAt.getTime()
      : new Date(idea.createdAt).getTime()

  return Number.isFinite(value) ? value : 0
}

const formatCreatedAt = (date: Date): string => {
  const currentYear = new Date().getFullYear()
  const format = date.getFullYear() === currentYear ? "d MMM" : "d MMM yyyy"
  return DateFns.format(date, format)
}

const iconButtonClass =
  "flex h-8 w-8 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-stone-900/[0.06] hover:text-stone-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/50 dark:text-stone-500 dark:hover:bg-white/[0.08] dark:hover:text-stone-200"

const Form = ({
  idea,
  deleteAndClose,
}: {
  idea: Idea
  deleteAndClose: () => void
}) => {
  const { model, commander, hub } = useNana()
  const [form, setForm] = useState<IdeaPaneFormData>({
    title: idea.title || "",
    memo: idea.memo || "",
    colour: idea.colour || "",
  })
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useOutsideClick(() => {
    setMenuOpen(false)
    return true
  }, menuOpen)

  // Close overflow menu when switching ideas
  useEffect(() => {
    setMenuOpen(false)
  }, [idea.id])

  const ideaNumber = useMemo(() => {
    const ideasByAge = Array.from(model.store.ideas.values()).sort((a, b) => {
      const timestampDelta = creationTimestamp(a) - creationTimestamp(b)
      if (timestampDelta !== 0) return timestampDelta

      return String(a.id).localeCompare(String(b.id))
    })

    const index = ideasByAge.findIndex((i) => i.id === idea.id)
    return index === -1 ? null : index + 1
  }, [model.store.ideas, idea.id])
  const createdAt = formatCreatedAt(idea.createdAt)

  useEffect(() => {
    const k = hub.on("model:idea-updated", ({ id, idea, originator }) => {
      if (originator === "editor") return
      if (id !== idea.id) return
      setForm({
        ...form,
        title: idea.title || "",
        memo: idea.memo || "",
        colour: idea.colour || "",
      })
    })

    return () => {
      hub.remove(k)
    }
  }, [idea])

  useEffect(() => {
    setForm({
      title: idea.title || "",
      memo: idea.memo || "",
      colour: idea.colour || "",
    })
  }, [idea])

  // Focus the title input when the idea is created
  const titleInputRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    if (idea && ideaIsEmpty(idea)) {
      const t = setTimeout(() => titleInputRef.current?.focus(), 100)
      return () => clearTimeout(t)
    }
  }, [idea])

  const updateField = (field: IdeaFieldName, value: string) => {
    commander.exec(
      new Commands.SetIdeaFieldCommand({
        id: idea.id,
        field: field,
        value: value,
        selectionPos: null,
      })
    )

    setForm({ ...form, [field]: value })
  }

  const closeWithEvent = () => {
    NavigationHistoryState.set_open_location(null)
  }

  return (
    <div id="idea-form" className="flex shrink-0 flex-col">
      {/* Identity bar */}
      <div className="flex flex-row items-center justify-between gap-3 border-b border-stone-200/70 px-4 py-2.5 dark:border-white/10">
        <div className="flex min-w-0 flex-row items-center gap-2.5">
          <label
            className={classNames(
              "h-7 w-7 shrink-0 cursor-pointer overflow-hidden border box-border shadow-sm transition-transform hover:scale-105",
              {
                "rounded-full": idea.type == "goal",
                "rounded-[3px]": idea.type != "goal",
              }
            )}
            style={{
              backgroundColor: form.colour || "#ffffff",
              borderColor: cellBorderColour(form.colour || "#ffffff"),
            }}
            title="Cell colour"
          >
            <input
              type="color"
              name="colour"
              className="opacity-0"
              value={form.colour}
              onChange={(e) => updateField("colour", e.target.value)}
            />
          </label>
          <input
            type="text"
            name="memo"
            value={form.memo}
            onChange={(e) => {
              const maxLength = idea?.type == "goal" ? 5 : 3
              const truncatedValue = [...e.target.value]
                .slice(0, maxLength)
                .join("")
              updateField("memo", truncatedValue)
            }}
            className="w-14 rounded-md border-0 bg-stone-900/[0.04] px-2 py-1 text-center text-[13px] font-medium tracking-wide text-stone-800 ring-1 ring-inset ring-stone-900/10 placeholder:font-normal placeholder:tracking-normal placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-sky-400/40 dark:bg-white/[0.06] dark:text-stone-100 dark:ring-white/10 dark:placeholder:text-stone-500 dark:focus:ring-sky-400/30"
            size={idea?.type == "goal" ? 5 : 3}
            placeholder={form.title ? form.title.slice(0, 3) : "Memo"}
            onKeyDown={(e) => {
              if (e.key == "Escape") {
                e.preventDefault()
                e.stopPropagation()
                closeWithEvent()
              }
            }}
          />
          <div className="ml-1 hidden items-center gap-1.5 text-[11px] font-medium tracking-wide text-stone-400 dark:text-stone-500 sm:flex">
            {ideaNumber && (
              <>
                <span>#{ideaNumber}</span>
                <span className="text-stone-300 dark:text-stone-600">·</span>
              </>
            )}
            <span>{createdAt}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {idea.id && (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                className={iconButtonClass}
                onClick={() => setMenuOpen(!menuOpen)}
                title="More actions"
                aria-label="More actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                data-testid="idea-pane-menu-button"
              >
                <EllipsisVerticalIcon className="h-5 w-5" strokeWidth={1.75} />
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 z-30 mt-1 w-40 origin-top-right overflow-hidden rounded-lg border border-stone-200/80 bg-white py-1 shadow-[0_12px_40px_rgba(15,23,42,0.16)] ring-1 ring-stone-900/[0.04] dark:border-white/10 dark:bg-gray-900 dark:shadow-[0_12px_40px_rgba(0,0,0,0.55)] dark:ring-white/10"
                  role="menu"
                  aria-label="Idea actions"
                >
                  <button
                    type="button"
                    role="menuitem"
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-stone-700 transition-colors hover:bg-stone-900/[0.05] focus:outline-none focus-visible:bg-stone-900/[0.05] dark:text-stone-200 dark:hover:bg-white/[0.06] dark:focus-visible:bg-white/[0.06]"
                    onClick={() => {
                      setMenuOpen(false)
                      deleteAndClose()
                    }}
                    data-testid="idea-pane-delete-button"
                  >
                    <TrashIcon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            className={iconButtonClass}
            onClick={closeWithEvent}
            title="Close"
            aria-label="Close idea pane"
          >
            <XMarkIcon className="h-5 w-5" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className="grow-wrap-idea-title" data-replicated-value={form.title}>
        <textarea
          name="title"
          value={form.title}
          ref={titleInputRef}
          onChange={(e) => {
            const maxLength = 255
            const truncatedValue = [...e.target.value]
              .slice(0, maxLength)
              .join("")
            updateField("title", truncatedValue)
          }}
          rows={1}
          placeholder={
            idea?.type == "goal"
              ? "Goal title..."
              : "Idea name or short description..."
          }
          className="resize-none border-0 bg-transparent text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-stone-500"
          onKeyDown={(e) => {
            if (e.key == "Escape") {
              e.preventDefault()
              e.stopPropagation()
              closeWithEvent()
            }
          }}
          data-testid="idea-title-input"
        />
      </div>
    </div>
  )
}

export default Form
